/** Kindra OS · Kiosk & Fullscreen Controls
 *  ─────────────────────────────────────────────────────────────────────
 *  MJ's mission-control monitor becomes JUST the OS — full screen, no
 *  browser chrome, no taskbar, edge-to-edge dashboard.
 *
 *  Exports:
 *    - useFullscreen()   — reactive Fullscreen API hook with vendor prefixes.
 *    - useKioskMode()    — fullscreen + auto-refresh + suppress-idle in one.
 *    - KioskControls     — the ⛶ / 🖥 buttons that sit in the top-right of
 *                          the OS Chrome header.
 *    - KioskExitPill     — the "Kiosk · Esc to exit" pill overlay.
 *
 *  Persistence: preferences live in localStorage under `kindra.kiosk.*`
 *  so the setup survives reloads and PWA relaunches. Never auto-enters
 *  fullscreen on load without a saved gesture — the browser blocks it.
 */
import React, { useCallback, useEffect, useState, useRef } from 'react';

const LS_KIOSK    = 'kindra.kiosk.enabled';
const LS_AUTOENTER = 'kindra.kiosk.autoenter_pref';

// ── Browser detection helpers ──────────────────────────────────────────
const _fsElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.msFullscreenElement || null;

const _requestFs = (el) => {
  const fn = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
  return fn ? fn.call(el) : Promise.reject(new Error('Fullscreen API not supported'));
};

const _exitFs = () => {
  const fn = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
  return fn ? fn.call(document) : Promise.resolve();
};

/** True if the app is being served as an installed PWA (no browser chrome). */
export const isStandalonePWA = () =>
  window.matchMedia('(display-mode: standalone)').matches
  || window.matchMedia('(display-mode: fullscreen)').matches
  || window.matchMedia('(display-mode: window-controls-overlay)').matches
  || window.navigator.standalone === true;  // iOS Safari legacy

// ── Fullscreen hook ────────────────────────────────────────────────────
export const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(!!_fsElement());
  useEffect(() => {
    const on = () => setIsFullscreen(!!_fsElement());
    document.addEventListener('fullscreenchange', on);
    document.addEventListener('webkitfullscreenchange', on);
    document.addEventListener('msfullscreenchange', on);
    return () => {
      document.removeEventListener('fullscreenchange', on);
      document.removeEventListener('webkitfullscreenchange', on);
      document.removeEventListener('msfullscreenchange', on);
    };
  }, []);
  const enter = useCallback(async () => {
    if (_fsElement()) return true;
    try { await _requestFs(document.documentElement); return true; }
    catch (e) { console.warn('[kiosk] enter fullscreen refused:', e); return false; }
  }, []);
  const exit = useCallback(async () => {
    if (!_fsElement()) return;
    try { await _exitFs(); } catch (e) { console.warn('[kiosk] exit fullscreen:', e); }
  }, []);
  const toggle = useCallback(() => (isFullscreen ? exit() : enter()), [isFullscreen, enter, exit]);
  return { isFullscreen, enter, exit, toggle,
           supported: typeof document.documentElement.requestFullscreen === 'function'
                   || typeof document.documentElement.webkitRequestFullscreen === 'function' };
};

// ── Kiosk mode hook ────────────────────────────────────────────────────
//   - Sets body[data-kiosk="true"] so CSS collapses margins + auto-hides
//     the top bar (revealed on hover).
//   - Suppresses the visibility-based idle refresh throttle so a wall
//     display keeps pulling fresh data even when tab is "backgrounded"
//     (which shouldn't happen in true fullscreen, but belt-and-braces).
//   - Broadcasts a `kindra:kiosk-refresh` custom event every 5 min for any
//     tile-hooks that want to force-fetch instead of relying on their own
//     interval.
//   - Persists preference so a reload keeps the intent; the hook offers
//     an `armAutoEnter()` callback the user can wire to a gesture so the
//     NEXT load automatically re-enters fullscreen on first user click.
export const useKioskMode = () => {
  const fs = useFullscreen();
  const [kioskOn, setKioskOn] = useState(() => {
    try { return localStorage.getItem(LS_KIOSK) === '1'; }
    catch { return false; }
  });
  const [autoEnterArmed, setAutoEnterArmed] = useState(() => {
    try { return localStorage.getItem(LS_AUTOENTER) === '1'; }
    catch { return false; }
  });
  const refreshTimer = useRef(null);

  // Body flag — CSS keys off this for the immersive layout.
  useEffect(() => {
    document.body.dataset.kiosk = kioskOn ? 'true' : 'false';
    return () => { delete document.body.dataset.kiosk; };
  }, [kioskOn]);
  useEffect(() => {
    document.body.dataset.fullscreen = fs.isFullscreen ? 'true' : 'false';
    return () => { delete document.body.dataset.fullscreen; };
  }, [fs.isFullscreen]);

  // Auto-refresh broadcast every 5 min while kiosk is on.
  useEffect(() => {
    if (!kioskOn) {
      if (refreshTimer.current) { clearInterval(refreshTimer.current); refreshTimer.current = null; }
      return undefined;
    }
    refreshTimer.current = setInterval(() => {
      window.dispatchEvent(new CustomEvent('kindra:kiosk-refresh', { detail: { at: Date.now() } }));
    }, 5 * 60 * 1000);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [kioskOn]);

  // Auto-enter on load: if the user armed it AND we're running as an
  // installed PWA (where fullscreen is granted on load without a gesture),
  // enter immediately. In a normal browser tab we DON'T force it — the
  // API requires a user gesture and would throw.
  useEffect(() => {
    if (!autoEnterArmed || fs.isFullscreen) return;
    if (isStandalonePWA()) {
      fs.enter();
    }
  }, [autoEnterArmed]);

  const enterKiosk = useCallback(async () => {
    try { localStorage.setItem(LS_KIOSK, '1'); } catch {}   // eslint-disable-line no-empty
    setKioskOn(true);
    await fs.enter();
  }, [fs]);

  const exitKiosk = useCallback(async () => {
    try { localStorage.setItem(LS_KIOSK, '0'); } catch {}   // eslint-disable-line no-empty
    setKioskOn(false);
    await fs.exit();
  }, [fs]);

  const armAutoEnter = useCallback((armed) => {
    setAutoEnterArmed(!!armed);
    try { localStorage.setItem(LS_AUTOENTER, armed ? '1' : '0'); } catch {}  // eslint-disable-line no-empty
  }, []);

  return { ...fs, kioskOn, enterKiosk, exitKiosk, autoEnterArmed, armAutoEnter, standalone: isStandalonePWA() };
};

// ── The visible controls in the OS Chrome top-right ───────────────────
const CTL_BTN = {
  background: 'transparent',
  border: '1px solid rgba(220,240,245,0.14)',
  color: '#DCF0F5',
  fontFamily: 'Inter',
  fontSize: 12,
  padding: '5px 10px',
  borderRadius: 999,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
};

export const KioskControls = ({ compact = false }) => {
  const k = useKioskMode();

  // Keyboard shortcut — F toggles fullscreen (matches F11-ish habit),
  // Shift+K toggles kiosk. Ignored inside inputs/textareas/contenteditable
  // so it doesn't hijack normal typing.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const inField = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (inField) return;
      if (e.key === 'F' || e.key === 'f') {
        // Guard against F+modifier combos (ctrl/alt/cmd) which usually mean something else.
        if (!e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); k.toggle(); }
      } else if ((e.key === 'K' || e.key === 'k') && e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        (k.kioskOn ? k.exitKiosk() : k.enterKiosk());
      } else if (e.key === 'Escape' && k.kioskOn && k.isFullscreen) {
        // Browser handles Esc exiting fullscreen; also flip our kiosk flag
        // so the tabs/topbar reappear tidily instead of us "still in kiosk" limbo.
        setTimeout(() => k.exitKiosk(), 20);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [k]);

  if (!k.supported) return null;

  return (
    <div data-testid="kiosk-controls" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <button data-testid="kiosk-fullscreen-toggle"
        onClick={k.toggle}
        title={`${k.isFullscreen ? 'Exit' : 'Enter'} fullscreen — press F, or Esc to exit`}
        aria-label={k.isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        style={{
          ...CTL_BTN,
          background: k.isFullscreen ? 'rgba(124,92,246,0.18)' : 'transparent',
          borderColor: k.isFullscreen ? 'rgba(124,92,246,0.6)' : CTL_BTN.border.replace('1px solid ', ''),
        }}>
        <span style={{ fontFamily: 'Inter', fontWeight: 700 }}>{k.isFullscreen ? '⛶' : '⛶'}</span>
        {!compact && <span>{k.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}</span>}
      </button>
      <button data-testid="kiosk-mode-toggle"
        onClick={() => (k.kioskOn ? k.exitKiosk() : k.enterKiosk())}
        title={k.kioskOn ? 'Exit kiosk mode' : 'Kiosk mode — fullscreen + live refresh (Shift+K)'}
        aria-pressed={k.kioskOn}
        style={{
          ...CTL_BTN,
          background: k.kioskOn ? 'rgba(0,229,195,0.16)' : 'transparent',
          borderColor: k.kioskOn ? 'rgba(0,229,195,0.55)' : CTL_BTN.border.replace('1px solid ', ''),
          color: k.kioskOn ? '#00E5C3' : CTL_BTN.color,
        }}>
        <span style={{ fontSize: 13 }}>🖥</span>
        {!compact && <span>{k.kioskOn ? 'Kiosk on' : 'Kiosk'}</span>}
      </button>
    </div>
  );
};

// ── Floating pill visible only in kiosk — "Esc to exit" ───────────────
export const KioskExitPill = () => {
  const k = useKioskMode();
  if (!k.kioskOn) return null;
  return (
    <div data-testid="kiosk-exit-pill"
      onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.35'; }}
      style={{
        position: 'fixed', bottom: 14, right: 14, zIndex: 400,
        opacity: 0.35, transition: 'opacity 200ms ease',
        background: 'rgba(11,5,38,0.85)', backdropFilter: 'blur(12px)',
        border: '1px solid rgba(0,229,195,0.35)', borderRadius: 999,
        padding: '5px 12px', fontFamily: 'Inter', fontSize: 10.5, fontWeight: 700,
        letterSpacing: '0.12em', textTransform: 'uppercase', color: '#00E5C3',
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)', pointerEvents: 'auto',
      }}>
      <button data-testid="kiosk-exit-pill-btn" onClick={k.exitKiosk}
        style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', font: 'inherit', letterSpacing: 'inherit', padding: 0 }}>
        Kiosk on · Esc / click to exit
      </button>
    </div>
  );
};

// ── Global CSS injected once — collapses margins + auto-hides topbar ──
export const KioskGlobalStyles = () => (
  <style>{`
    /* Fullscreen + kiosk: no wasted margins. Grid expands edge-to-edge. */
    body[data-fullscreen="true"] .max-w-\\[1280px\\] {
      max-width: 100% !important;
    }
    body[data-fullscreen="true"] main,
    body[data-fullscreen="true"] .desk-tile-wrap {
      padding-left: 12px !important;
      padding-right: 12px !important;
    }

    /* Kiosk-specific: auto-hide the top nav until hover, so the monitor is
       purely dashboard. Peek strip along the top pulls it back into view. */
    body[data-kiosk="true"] header.sticky {
      transform: translateY(-100%);
      transition: transform 240ms ease;
    }
    body[data-kiosk="true"] header.sticky:hover,
    body[data-kiosk="true"] header.sticky:focus-within {
      transform: translateY(0);
    }
    body[data-kiosk="true"]::before {
      content: '';
      position: fixed; top: 0; left: 0; right: 0; height: 8px;
      z-index: 40; pointer-events: none;
      background: linear-gradient(180deg, rgba(0,229,195,0.28), transparent);
      opacity: 0.7;
    }
    /* Hover peek — a 24px strip at the top gets the topbar back cleanly. */
    body[data-kiosk="true"] .kiosk-peek {
      position: fixed; top: 0; left: 0; right: 0; height: 24px; z-index: 45;
    }
    body[data-kiosk="true"] .kiosk-peek:hover ~ header.sticky {
      transform: translateY(0);
    }
    /* Respect iOS safe-areas even in fullscreen */
    body[data-fullscreen="true"] {
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
    }
  `}</style>
);
